import { generateHTML, getSchema } from '@tiptap/core';
import type { JSONContent } from '@tiptap/core';
import type { Doc as YDoc } from 'yjs';

import {
  DEFAULT_FOOTER_SETTINGS,
  DEFAULT_HEADER_SETTINGS,
  type DocumentFooterSettings,
  type DocumentHeaderSettings,
  readDocumentSettings,
  writeDocumentSettings,
} from 'containers/Tiptap/components/tiptap-extension/document-settings-yjs';
import { headerFooterExtensions } from 'containers/Tiptap/lib/header-footer-extensions';
import { pruneToSchema, sanitizeHeaderFooterNode } from 'containers/Tiptap/lib/import-sanitize';
import type { ImportedHeaderFooter } from 'containers/Tiptap/lib/import-utils';

export const MAX_IMPORTED_IMAGE_HEIGHT_PX = 96;

const NUMPAGES_TOKEN = /\{numpages\}/g;
const TAB_RUN = /\t| {4,}/;
const TAB_RUN_GLOBAL = new RegExp(TAB_RUN, 'g');

type Attrs = Record<string, unknown>;

const toNumber = (value: unknown): number | null => {
  const parsed = typeof value === 'string' ? parseFloat(value) : value;
  return typeof parsed === 'number' && Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const capImage = (node: JSONContent): JSONContent => {
  const attrs = (node.attrs ?? {}) as Attrs;
  const height = toNumber(attrs.height);
  if (height === null || height <= MAX_IMPORTED_IMAGE_HEIGHT_PX) return node;
  const width = toNumber(attrs.width);
  const next: Attrs = { ...attrs, height: MAX_IMPORTED_IMAGE_HEIGHT_PX };
  if (width !== null) next.width = Math.round((width / height) * MAX_IMPORTED_IMAGE_HEIGHT_PX);
  return { ...node, attrs: next };
};

const normalizeNode = (node: JSONContent): JSONContent => {
  if (node.type === 'text' && node.text) return { ...node, text: node.text.replace(NUMPAGES_TOKEN, '{total}') };
  if (node.type === 'image') return capImage(node);
  if (!Array.isArray(node.content)) return node;
  return { ...node, content: node.content.map(normalizeNode) };
};

const wrapOrphanImages = (doc: JSONContent): JSONContent => ({
  ...doc,
  content: (doc.content ?? []).map((child) =>
    child.type === 'image' ? { type: 'paragraph', content: [child] } : child
  ),
});

const paragraphText = (paragraph: JSONContent): string =>
  (paragraph.content ?? []).map((child) => (child.type === 'text' ? (child.text ?? '') : '\u0000')).join('');

const splitInlineOnTabs = (inline: JSONContent[]): JSONContent[][] => {
  const segments: JSONContent[][] = [[]];
  inline.forEach((child) => {
    if (child.type !== 'text' || !child.text || !TAB_RUN.test(child.text)) {
      segments[segments.length - 1].push(child);
      return;
    }
    child.text.split(TAB_RUN_GLOBAL).forEach((part, index) => {
      if (index > 0) segments.push([]);
      if (part) segments[segments.length - 1].push({ ...child, text: part });
    });
  });
  return segments.filter((segment, index) => index === 0 || segment.length > 0);
};

const zoneCell = (inline: JSONContent[], textAlign: 'left' | 'right'): JSONContent => ({
  type: 'tableCell',
  content: [{ type: 'paragraph', attrs: { textAlign }, content: inline }],
});

export function splitTabsIntoZones(doc: JSONContent): JSONContent {
  const blocks = doc.content ?? [];
  if (blocks.length !== 1 || blocks[0].type !== 'paragraph') return doc;
  if (!TAB_RUN.test(paragraphText(blocks[0]))) return doc;
  const segments = splitInlineOnTabs(blocks[0].content ?? []);
  if (segments.length < 2) return doc;
  const left = segments.slice(0, -1).flatMap((segment, index) => (index > 0 ? [{ type: 'text', text: ' ' }, ...segment] : segment));
  const right = segments[segments.length - 1];
  return {
    ...doc,
    content: [
      {
        type: 'table',
        content: [{ type: 'tableRow', content: [zoneCell(left, 'left'), zoneCell(right, 'right')] }],
      },
    ],
  };
};

export function slotToHtml(doc: JSONContent | null): string {
  if (!doc) return '';
  const schema = getSchema(headerFooterExtensions);
  const prepared = splitTabsIntoZones(wrapOrphanImages(normalizeNode(sanitizeHeaderFooterNode(doc))));
  const pruned = pruneToSchema(prepared, schema);
  return generateHTML(pruned, headerFooterExtensions);
}

export interface ImportedHeaderFooterSettings {
  header: DocumentHeaderSettings;
  footer: DocumentFooterSettings;
}

export function buildHeaderFooterSettings(imported: ImportedHeaderFooter): ImportedHeaderFooterSettings {
  const { differentFirstPage } = imported;
  return {
    header: {
      ...DEFAULT_HEADER_SETTINGS,
      content: slotToHtml(imported.header),
      differentFirstPage,
      variants: { first: slotToHtml(imported.headerFirstPage) },
    },
    footer: {
      ...DEFAULT_FOOTER_SETTINGS,
      content: slotToHtml(imported.footer),
      differentFirstPage,
      variants: { first: slotToHtml(imported.footerFirstPage) },
    },
  };
}

export function writeImportedHeaderFooter(ydoc: YDoc, imported: ImportedHeaderFooter | null | undefined): void {
  if (!imported) return;
  const existing = readDocumentSettings(ydoc);
  if (existing.header?.content || existing.footer?.content) return;
  writeDocumentSettings(ydoc, buildHeaderFooterSettings(imported));
}
